import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQuery } from '@tanstack/react-query'
import { 
 Sparkles, 
 RotateCcw, 
 Copy, 
 Check, 
 Download,
 Save,
 Wand2,
 Zap,
 Clock,
 BarChart3
} from 'lucide-react'
import { useEditorStore } from '../stores'
import { humanizeApi, stylesApi, historyApi } from '../services/api'
import { 
 Button, 
 Card, 
 TextArea, 
 StyleSelector, 
 Badge,
 EmptyState,
 SkeletonText,
 showToast,
} from '../components/ui'
import { SelectOption } from '../components/ui/Select'
import { useCopyToClipboard, useWordCount } from '../hooks'
import { calculateReadingTime, downloadAsFile } from '../utils'

const defaultStyles: SelectOption[] = [
 { value: 'casual', label: '轻松随意', description: '友好、自然、像日常对话一样' },
 { value: 'formal', label: '正式专业', description: '专业、学术、商务正式场合' },
 { value: 'creative', label: '创意活泼', description: '富有表现力和想象力' },
 { value: 'technical', label: '技术严谨', description: '清晰、准确、专业术语' },
 { value: 'persuasive', label: '说服力强', description: '有说服力、引人入胜' },
 { value: 'narrative', label: '叙事风格', description: '讲故事、情节化表达' },
 { value: 'academic', label: '学术论文', description: '严谨、引用、学术格式' },
 { value: 'social', label: '社交媒体', description: '流行语、简洁、互动感' },
]

export const EditorPage = () => {
  const {
    inputText,
    setInputText,
    outputText,
    selectedStyle,
    setSelectedStyle,
    isProcessing,
    setIsProcessing,
    lastResult,
    setLastResult,
    setOutputText,
    reset,
  } = useEditorStore()
 
  const { copy, copied } = useCopyToClipboard()
  const inputWordCount = useWordCount(inputText)
  const outputWordCount = useWordCount(outputText)
  const [isSampleLoading, setIsSampleLoading] = useState(false)
 
  // Fetch available styles
  const { data: styles } = useQuery({
    queryKey: ['styles'],
    queryFn: stylesApi.getAll,
    initialData: defaultStyles.map(s => ({ 
      id: s.value, 
      name: s.label, 
      description: s.description || '' 
    })),
  })
  
  // Transform mutation
  const transformMutation = useMutation({
    mutationFn: humanizeApi.transform,
    onMutate: () => {
      setIsProcessing(true)
    },
    onSuccess: (data) => {
      setOutputText(data.humanized)
      setLastResult(data)
      setIsProcessing(false)
      showToast.success('文案自然化成功！')
    },
    onError: (error: { message: string }) => {
      setIsProcessing(false)
      showToast.error(error.message || '转换失败，请重试')
    },
  })
  
  // Save to history mutation
  const saveMutation = useMutation({
    mutationFn: historyApi.create,
    onSuccess: () => {
      showToast.success('已保存到历史记录！')
    },
    onError: () => {
      showToast.error('保存失败，请重试')
    },
  })
  
  const handleTransform = useCallback(() => {
    if (!inputText.trim()) {
      showToast.warning('请输入需要转换的文本')
      return
    }
    
    transformMutation.mutate({
      text: inputText,
      style: selectedStyle,
    })
  }, [inputText, selectedStyle, transformMutation])
  
  const handleLoadSample = useCallback(async () => {
    setIsSampleLoading(true)
    try {
      const sample = await humanizeApi.getSample(selectedStyle)
      setInputText(sample.original)
      showToast.info('示例文案已加载')
    } catch {
      // Fallback text
      setInputText(
        '在当今数字化转型的浪潮中，人工智能技术正在以前所未有的速度重塑各行各业的发展格局。企业纷纷拥抱AI，希望通过技术创新提升效率、降低成本、增强竞争力。'
      )
    } finally {
      setIsSampleLoading(false)
    }
  }, [selectedStyle, setInputText])
  
  const handleSave = useCallback(() => {
    if (!lastResult) return
    saveMutation.mutate({
      original: lastResult.original,
      humanized: lastResult.humanized,
      style: lastResult.style,
    })
  }, [lastResult, saveMutation])
  
  const handleDownload = useCallback(() => {
    if (!outputText) return
    const fileName = `人类化文案-${selectedStyle}-${Date.now()}.txt`
    downloadAsFile(outputText, fileName)
    showToast.success('文件已下载！')
  }, [outputText, selectedStyle])
  
  const styleOptions: SelectOption[] = styles?.map(s => ({
    value: s.id || s.name.toLowerCase(),
    label: s.name,
    description: s.description || '',
  })) || defaultStyles
 
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <div className="flex items-center gap-4 mb-3">
          <motion.div 
            whileHover={{ rotate: 15, scale: 1.1 }}
            className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500/20 to-accent-purple/20 flex items-center justify-center"
          >
            <Wand2 className="w-6 h-6 text-brand-400" />
          </motion.div>
          <div>
            <h1 className="text-3xl font-bold text-dark-text-primary">文案自然化工具</h1>
            <p className="text-sm text-dark-text-secondary mt-1">
              将AI生成的内容转化为自然、像真人撰写的文字
            </p>
          </div>
        </div>
      </motion.div>
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card padding="lg" className="glass card-float h-full">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500/80" />
                <h2 className="text-lg font-semibold text-dark-text-primary">原始文本（带AI味）</h2>
              </div>
              <Badge variant="default" size="sm" className="glass">
                <BarChart3 className="w-3 h-3 mr-1" />
                {inputWordCount} 字
              </Badge>
            </div>
            
            <TextArea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="在这里粘贴你的AI生成文案..."
              rows={14}
              showCount
              maxLength={5000}
              className="glass min-h-[320px]"
            />
            
            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setInputText('')}
                  disabled={!inputText}
                  className="glass-hover"
                >
                  清空
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLoadSample}
                  isLoading={isSampleLoading}
                  className="glass-hover"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  加载示例
                </Button>
              </div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RotateCcw className="w-4 h-4" />}
                onClick={reset}
              >
                重置
              </Button>
            </div>
          </Card>
        </motion.div>
        
        {/* Output Section */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card padding="lg" className="glass card-float h-full relative overflow-hidden">
            {/* 动态光晕背景 */}
            {outputText && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 pointer-events-none"
              >
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-purple/5 rounded-full blur-3xl" />
              </motion.div>
            )}
            
            <div className="flex items-center justify-between mb-5 relative z-10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <h2 className="text-lg font-semibold text-dark-text-primary">自然化结果（人类感）</h2>
              </div>
              <div className="flex items-center gap-2">
                {outputText && (
                  <Badge variant="success" size="sm" className="glass glow">
                    <Clock className="w-3 h-3 mr-1" />
                    {outputWordCount} 字 · 约{calculateReadingTime(outputText)}分钟阅读
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="relative min-h-[320px] z-10">
              {isProcessing ? (
                <div className="space-y-4 py-12">
                  <SkeletonText lines={10} />
                  <div className="flex items-center justify-center mt-8">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <Sparkles className="w-8 h-8 text-brand-400" />
                    </motion.div>
                    <span className="ml-3 text-dark-text-secondary">正在自然化处理中...</span>
                  </div>
                </div>
              ) : outputText ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={outputText}
                    initial={{ opacity: 0, y: 20, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    className="space-y-5"
                  >
                    <div className="bg-dark-bg-tertiary/50 border border-dark-border rounded-xl p-5 min-h-[280px] relative group">
                      <p className="text-dark-text-primary whitespace-pre-wrap leading-relaxed text-lg">
                        {outputText}
                      </p>
                      
                      {/* 成功提示 */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute top-3 right-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-400" />
                        </div>
                      </motion.div>
                    </div>
                    
                    {/* Stats */}
                    {lastResult && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="grid grid-cols-3 gap-4 p-4 glass rounded-xl"
                      >
                        <div className="text-center">
                          <p className="text-2xl font-bold text-brand-400">
                            {Math.round(lastResult.confidenceScore * 100)}%
                          </p>
                          <p className="text-xs text-dark-text-secondary mt-1">置信度</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-400">
                            {lastResult.tokensChanged}
                          </p>
                          <p className="text-xs text-dark-text-secondary mt-1">修改处</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-accent-cyan">
                            {lastResult.processingTime.toFixed(2)}s
                          </p>
                          <p className="text-xs text-dark-text-secondary mt-1">耗时</p>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                </AnimatePresence>
              ) : (
                <EmptyState
                  type="results"
                  actionLabel="左侧输入文本，点击转换"
                  onAction={() => document.querySelector('textarea')?.focus()}
                />
              )}
            </div>
            
            {/* Output Actions */}
            {outputText && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-end gap-2 mt-5 relative z-10"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  onClick={() => copy(outputText)}
                  className="glass-hover"
                >
                  {copied ? '已复制' : '复制'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Download className="w-4 h-4" />}
                  onClick={handleDownload}
                  className="glass-hover"
                >
                  下载
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Save className="w-4 h-4" />}
                  onClick={handleSave}
                  isLoading={saveMutation.isPending}
                >
                  保存到历史
                </Button>
              </motion.div>
            )}
          </Card>
        </motion.div>
      </div>
      
      {/* Style Selector */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card padding="lg" className="mt-6 glass">
          <StyleSelector
            label="选择写作风格"
            value={selectedStyle}
            onChange={setSelectedStyle}
            options={styleOptions}
          />
        </Card>
      </motion.div>
      
      {/* Transform Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 flex justify-center"
      >
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            size="lg"
            onClick={handleTransform}
            isLoading={isProcessing}
            leftIcon={<Sparkles className="w-5 h-5" />}
            disabled={!inputText.trim()}
            className="px-16 py-4 text-lg btn-3d glow"
          >
            {isProcessing ? '自然化处理中...' : '开始去AI化!' }
          </Button>
        </motion.div>
      </motion.div>
    </div>
  )
}
